// Member-table column model + persistence. The Members page renders this
// list left-to-right; pinned columns (first_name, last_name) always appear
// first and can't be hidden or reordered. Everything else is draggable and
// toggleable, with prefs persisted per-org in localStorage so the column
// layout sticks across tabs and reloads.

export type ColumnId =
  | 'first_name'
  | 'last_name'
  | 'personal_id'
  | 'email'
  | 'phone'
  | 'street_address'
  | 'postal_code'
  | 'city'
  | 'membership';

export interface ColumnPref {
  id: ColumnId;
  visible: boolean;
}

// Pinned columns are *always* shown first and never moved or hidden. They
// live outside the persisted prefs list — `prefs` only contains the
// reorderable/toggleable columns. Treating them as a separate constant
// keeps the drag-drop code from accidentally allowing them to move.
export const PINNED_COLUMNS: readonly ColumnId[] = ['first_name', 'last_name'];

// Default order + visibility for the non-pinned columns. New columns are
// visible by default — users hide what they don't need via EditColumnsDropdown.
// The free-text `team` column was removed when teams became their own table;
// proper team display is reintroduced in a follow-up alongside the Teams page.
export const DEFAULT_COLUMN_PREFS: readonly ColumnPref[] = [
  { id: 'personal_id',    visible: true },
  { id: 'email',          visible: true },
  { id: 'phone',          visible: true },
  { id: 'street_address', visible: true },
  { id: 'postal_code',    visible: true },
  { id: 'city',           visible: true },
  { id: 'membership',     visible: true },
];

// i18n key for each column header. Kept centralized so EditColumnsDropdown
// and the table both use the same translation.
export const COLUMN_LABEL_KEY: Record<ColumnId, string> = {
  first_name:     'members.column.firstName',
  last_name:      'members.column.lastName',
  personal_id:    'members.column.personalId',
  email:          'members.column.email',
  phone:          'members.column.phone',
  street_address: 'members.column.streetAddress',
  postal_code:    'members.column.postalCode',
  city:           'members.column.city',
  membership:     'members.column.membership',
};

const storageKey = (slug: string) => `members.columns.v1.${slug}`;

export function loadColumnPrefs(slug: string): ColumnPref[] {
  try {
    const raw = localStorage.getItem(storageKey(slug));
    if (!raw) return [...DEFAULT_COLUMN_PREFS];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [...DEFAULT_COLUMN_PREFS];

    // Coerce: keep only known ids, dedupe, append any missing defaults so a
    // newer release that adds a column doesn't hide it from existing users.
    const knownIds = new Set<ColumnId>(DEFAULT_COLUMN_PREFS.map((p) => p.id));
    const seen = new Set<ColumnId>();
    const out: ColumnPref[] = [];
    for (const entry of parsed) {
      if (!entry || typeof entry !== 'object') continue;
      const id = (entry as { id?: unknown }).id;
      const visible = (entry as { visible?: unknown }).visible;
      if (typeof id !== 'string' || !knownIds.has(id as ColumnId)) continue;
      if (seen.has(id as ColumnId)) continue;
      seen.add(id as ColumnId);
      out.push({ id: id as ColumnId, visible: visible !== false });
    }
    for (const def of DEFAULT_COLUMN_PREFS) {
      if (!seen.has(def.id)) out.push({ ...def });
    }
    return out;
  } catch {
    return [...DEFAULT_COLUMN_PREFS];
  }
}

export function saveColumnPrefs(slug: string, prefs: ColumnPref[]): void {
  try {
    localStorage.setItem(storageKey(slug), JSON.stringify(prefs));
  } catch {
    // localStorage can fail (private mode, quota); column prefs are
    // best-effort UX. Silent fail is acceptable here.
  }
}

export function resetColumnPrefs(slug: string): ColumnPref[] {
  try {
    localStorage.removeItem(storageKey(slug));
  } catch {
    // ignored
  }
  return [...DEFAULT_COLUMN_PREFS];
}

// visibleColumnsInOrder returns the full ordered list of columns to render
// (pinned first, then visible non-pinned in user-set order).
export function visibleColumnsInOrder(prefs: readonly ColumnPref[]): ColumnId[] {
  return [
    ...PINNED_COLUMNS,
    ...prefs.filter((p) => p.visible).map((p) => p.id),
  ];
}
